import {useEffect, useMemo, useState} from 'react';
import {ColumnData, ColumnTypes} from './types';
import {getOptions, getTableField, validateValue} from './utils';
import {useSelector} from 'react-redux';
import {selectDictionaries} from 'slices';
import {useDictionary} from 'utils/hooks/useDictionary';
import styles from './NewTable.module.scss';
import {usePrevious} from '@core/utils/hooks';
import {Tooltip} from '@mui/material';
import {ErrorTooltip} from './components/ErrorTooltip';

type TableEditCellProps = {
    CustomComponent?: any;
    customProps?: Record<string, any>;
    column: ColumnData;
    values: Record<string, any>;
    setValue: (key: string, newValue: any) => void;
    requiredConfig?: string[];
    initialDictKeyValue?: any;
    onValidationErrorChange?: (error: string | null) => void;
};

const TableEditCell = ({
    column,
    values,
    setValue,
    CustomComponent,
    customProps,
    requiredConfig = [],
    initialDictKeyValue,
    onValidationErrorChange,
}: TableEditCellProps) => {
    const [validationError, setValidationError] = useState<string | null>(null);
    const dictionaries = useSelector(selectDictionaries);
    // Для присвоения dictValueKey и dictIdKey по умолчанию для enumDict
    const formattedColumn = {
        ...column,
        dictValueKey: column.dictValueKey || (column.enumDict ? 'displayName' : 'value'),
        dictIdKey: column.dictIdKey || (column.enumDict ? 'code' : 'value'),
    };

    const currentValue = useMemo(() => values[formattedColumn.id], [formattedColumn.id, values]);

    // значение влияющего поля
    const dictKeyPropertyValue = useMemo(() => {
        return formattedColumn?.dictKeyProperty ? values[formattedColumn.dictKeyProperty] : null;
    }, [formattedColumn?.dictKeyProperty, values]);
    const prevKeyPropertyValue = usePrevious(dictKeyPropertyValue);

    // инициируем запрос за данными для зависимого поля только при наличии значений в влияющем поле
    const needQuery = useMemo(() => {
        if (formattedColumn?.keyTableAttr) {
            return true;
        }
        if (formattedColumn.dictName && formattedColumn?.dictKeyProperty && dictKeyPropertyValue) {
            return true;
        }
        return false;
    }, [
        formattedColumn?.dictKeyProperty,
        formattedColumn.dictName,
        formattedColumn?.keyTableAttr,
        dictKeyPropertyValue,
    ]);

    useDictionary({
        code: formattedColumn.dictName,
        key: formattedColumn?.keyTableAttr ? undefined : values[formattedColumn?.dictKeyProperty!],
        need: needQuery,
        isDictNameWithKey: true,
    });
    const dictCode = useMemo(() => {
        if (formattedColumn.dictName) {
            // Если есть keyTableAttr, это значит, что есть двустороняя связь, поэтому устанавливать dictKeyPropertyValue не нужно для справочника,
            // даже если оно есть, так как в данном случае логика рабоатет иначе - все данные уже есть, их не надо перезапрашивать по ключу от родителя
            if (formattedColumn?.keyTableAttr) {
                return formattedColumn.dictName;
            }
            return dictKeyPropertyValue
                ? `${formattedColumn.dictName}-${dictKeyPropertyValue}`
                : formattedColumn.dictName;
        }
        return '';
    }, [formattedColumn.dictName, formattedColumn?.keyTableAttr, dictKeyPropertyValue]);

    const dictionary = dictCode ? dictionaries[dictCode] : undefined;

    // Сбрасываем значение зависимого поля при ИЗМЕНЕНИИ значения влияющего поля (при условии, что словарь загружен для первого рендера)
    useEffect(() => {
        // Если есть keyTableAttr, то проверяем, является ли выбранное значение валидным. Если нет, то сбрасываем
        if (dictionary && formattedColumn?.keyTableAttr) {
            const options = getOptions({dictionary, column: formattedColumn, values});
            const hasValidOptions = options.find(
                (el) => el[formattedColumn.dictValueKey || 'value'] === currentValue,
            );
            if (hasValidOptions) {
                return;
            } else {
                setValue(formattedColumn.id, null);
                return;
            }
        }
        if (
            prevKeyPropertyValue === dictKeyPropertyValue ||
            (prevKeyPropertyValue === undefined && initialDictKeyValue === dictKeyPropertyValue)
        ) {
            return;
        }
        if (dictionary && formattedColumn?.dictKeyProperty) {
            setValue(formattedColumn.id, null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formattedColumn?.dictKeyProperty,
        formattedColumn.id,
        formattedColumn?.keyTableAttr,
        currentValue,
        dictKeyPropertyValue,
        dictionary,
        initialDictKeyValue,
        prevKeyPropertyValue,
        setValue,
    ]);

    // Сбрасываем значение зависимого поля при ОБНУЛЕНИИ значения влияющего поля
    useEffect(() => {
        if (
            currentValue &&
            formattedColumn.dictName &&
            formattedColumn.dictKeyProperty &&
            !dictKeyPropertyValue
        ) {
            setValue(formattedColumn.id, null);
        }
    }, [
        formattedColumn.dictKeyProperty,
        formattedColumn.dictName,
        formattedColumn.id,
        dictKeyPropertyValue,
        setValue,
        currentValue,
    ]);

    useEffect(() => {
        // Если поле имеет словарь, и он неизменяемый, а значение отсутствует в текущем словаре - сбрасываем
        if (!currentValue) return;

        // Выход, если нет словаря или словарь изменяемый
        if (!formattedColumn?.dictName || formattedColumn?.dictIsChangeable || !dictionary) return;
        // Проверка: если тип OBJECT
        if (formattedColumn.type === ColumnTypes.OBJECT) {
            console.warn('Unable to determine that value is in dictionary');
            return;
        }

        // Получаем ключ для значения словаря. Так как мы ищем по полю, которое является значением, а не отображением
        const dictIdKey = formattedColumn.dictIdKey || 'value';

        // Проверка для типа ARRAY
        if (formattedColumn.type === ColumnTypes.ARRAY) {
            const dictValuesSet = new Set(dictionary.map((it) => it[dictIdKey].toString())); // Множество для быстрого поиска
            const isDictHasEveryElement = currentValue.every((el: any) =>
                dictValuesSet.has(el.toString()),
            );

            if (!isDictHasEveryElement) {
                setValue(formattedColumn.id, null);
            }
            return;
        }

        // Функция проверки, есть ли элемент в словаре
        const isValueInDictionary = (value: any) =>
            dictionary.some((el) => el[dictIdKey].toString() === value.toString());

        // Проверка для всех остальных типов
        if (!isValueInDictionary(currentValue)) {
            setValue(formattedColumn.id, null);
        }
    }, [
        formattedColumn.type,
        formattedColumn.dictName,
        formattedColumn?.dictIsChangeable,
        formattedColumn.dictIdKey,
        formattedColumn.dictValueKey,
        formattedColumn.id,
        currentValue,
        dictionary,
        setValue,
    ]);

    // Блокируем поле при обновлении значений (новый запрос) из-за изменений влияющего поля
    const disableField = useMemo(() => {
        if (
            formattedColumn?.dictName &&
            formattedColumn?.dictKeyProperty &&
            !formattedColumn?.keyTableAttr
        ) {
            return dictKeyPropertyValue ? !dictionaries[dictCode]?.length : true;
        }
        return false;
    }, [
        formattedColumn?.dictName,
        formattedColumn?.dictKeyProperty,
        formattedColumn?.keyTableAttr,
        dictKeyPropertyValue,
        dictionaries,
        dictCode,
    ]);

    const required = requiredConfig.some((key) => key === formattedColumn.id);

    useEffect(() => {
        const error = validateValue(currentValue, column);

        setValidationError(error);
        if (onValidationErrorChange) {
            onValidationErrorChange(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentValue, column]);

    return (
        <div className={styles.editCell}>
            <Tooltip
                title={<ErrorTooltip validationError={validationError} />}
                slotProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: 'transparent',
                        },
                    },
                }}
            >
                {getTableField({
                    valueKey: column.id,
                    values: values,
                    setValue,
                    column,
                    disabled: disableField,
                    dictionary: dictionary,
                    CustomComponent,
                    customProps: {...customProps, dictCode},
                    required,
                    validationError,
                })}
            </Tooltip>
        </div>
    );
};

export default TableEditCell;
